/**
 * Mapa de modulos da plataforma.
 *
 * A ordem, os rotulos e os icones vem do rail do protótipo
 * (`docs/design-slices/_shell.html:1934-1950`). Cada modulo carrega tambem o
 * que o explorer do shell precisa mostrar quando ele esta ativo: titulo,
 * placeholder da busca, rotulo do botao criar e os chips de filtro rapido
 * (protótipo: `_shell.html:2895` createLabels, `:2912` searchPh,
 * `:2282-2290` chipsByModule).
 *
 * Duas divergencias deliberadas em relacao ao protótipo:
 * - o rail tem UMA entrada para o trabalho, chamada **Trabalho**
 *   (SPEC-20261003-1059). O protótipo tem "Work Items" e "Kanbans" separados;
 *   aqui os quadros sao um modo de ver os itens, nao outra funcionalidade. O
 *   modulo continua com o id `kanbans` e a rota `/kanbans` para nao mexer em
 *   tudo o que aponta para la.
 * - O protótipo nao tem "Inicio" no rail; aqui `/home` continua acessivel
 *   pelo clique no logo.
 *
 * `status: 'ready'` significa que a tela existe; `'planned'` renderiza o
 * placeholder com a referencia da spec.
 */
export type ModuleStatus = 'ready' | 'planned'

export type ModuleEntry = {
  id: string
  labelKey: string
  fallbackLabel: string
  icon: string
  route: string
  specId: string
  status: ModuleStatus
  /** Descreve o que a tela faz — usado no placeholder e nos tooltips do rail. */
  summary: string
  /** Placeholder do campo de busca do explorer quando este modulo esta ativo. */
  searchPlaceholder: string
  /** Titulo do botao "+" do explorer. */
  createLabel: string
  /** Chips de filtro rapido abaixo da busca. Vazio = modulo sem chips. */
  chips: readonly string[]
  /**
   * A tela traz o proprio explorer e o shell nao deve renderizar o dele.
   *
   * Vale para Itens de Trabalho e Quadros: o usuario determinou em
   * 2026-08-08 que essas duas telas ficam como estao ("a implementação está
   * ok"), entao elas seguem com o explorer proprio mesmo o protótipo pondo o
   * explorer no shell.
   */
  ownExplorer?: boolean
  /**
   * Modulo que tem explorer proprio mas nao ganha icone no rail.
   *
   * Notificacoes e o caso: no protótipo ela tem lista, busca e chips no
   * explorer (`_shell.html:278-296`, `:2291-2294`, `:2912`), mas o acesso e
   * pelo **sino do rodape do rail** (`:37-41`) — nao por um item da lista.
   */
  railHidden?: boolean
  /**
   * Fica DENTRO do ícone de outro módulo: na barra, quem acende é ele
   * (SPEC-20261010-1216-empresas-e-pessoas-num-icone — Pessoas é uma aba de Empresas).
   */
  dentroDe?: string
}

export const MODULES: readonly ModuleEntry[] = [
  /*
   * TRABALHO (SPEC-20261003-1059): Itens de Trabalho e Quadros eram duas
   * entradas para a mesma coisa — a segunda mostra os MESMOS itens arrumados em
   * lanes. Viraram uma, com o ícone que era dos itens; os quadros são recortes
   * dentro dela, e o nó "Itens de Trabalho" do explorer é a visão sem recorte.
   */
  {
    id: 'kanbans',
    labelKey: 'nav.trabalho',
    fallbackLabel: 'Trabalho',
    icon: 'fas fa-tasks',
    route: '/kanbans',
    specId: 'KAN-01',
    status: 'ready',
    summary: 'Itens de Trabalho em lista, quadro e planilha.',
    searchPlaceholder: 'Buscar quadros',
    createLabel: 'Novo quadro',
    chips: ['Meus quadros', 'Todos', 'Favoritos'],
    ownExplorer: true,
  },
  {
    id: 'calendar',
    labelKey: 'nav.calendar',
    fallbackLabel: 'Calendário',
    icon: 'far fa-calendar-alt',
    route: '/calendar',
    specId: 'v2',
    status: 'ready',
    summary: 'Grade do mês e agenda do dia.',
    searchPlaceholder: 'Buscar eventos',
    createLabel: 'Novo evento',
    chips: [],
  },
  {
    id: 'meetings',
    labelKey: 'nav.meetings',
    fallbackLabel: 'Reuniões',
    icon: 'fas fa-video',
    route: '/meetings',
    specId: 'v2',
    status: 'ready',
    summary: 'Agenda de reuniões, dock de chamada e notas vinculadas.',
    searchPlaceholder: 'Buscar reuniões',
    createLabel: 'Agendar reunião',
    chips: [],
  },
  /*
   * ESCRITÓRIO (SPEC-20261009-2000): tabuleiro em pixel art com as salas de
   * chamada. Em teste: só aparece com o toggle ESCRITORIO ligado (ver
   * `module-gate.tsx`).
   */
  {
    id: 'office',
    labelKey: 'nav.office',
    fallbackLabel: 'Escritório',
    icon: 'fas fa-layer-group',
    route: '/office',
    specId: 'SPEC-20261009-2000',
    status: 'ready',
    summary: 'As salas de chamada do time num tabuleiro.',
    searchPlaceholder: '',
    createLabel: '',
    chips: [],
    ownExplorer: true,
  },
  {
    id: 'mail',
    labelKey: 'nav.mail',
    fallbackLabel: 'E-mail',
    icon: 'far fa-envelope',
    route: '/mail',
    specId: 'v2',
    status: 'ready',
    summary: 'Caixa de entrada integrada, opcional por membro.',
    searchPlaceholder: 'Buscar e-mails',
    createLabel: 'Escrever',
    chips: [],
  },
  {
    id: 'docs',
    labelKey: 'nav.docs',
    fallbackLabel: 'Documentação',
    icon: 'fas fa-book',
    route: '/docs',
    specId: 'v2',
    status: 'ready',
    summary: 'Documentos colaborativos com blocos e checklists.',
    searchPlaceholder: 'Buscar documentos',
    createLabel: 'Novo documento',
    chips: [],
  },
  {
    id: 'apps',
    labelKey: 'nav.apps',
    fallbackLabel: 'Aplicações',
    icon: 'fas fa-th-large',
    route: '/apps',
    specId: 'SPEC-20260812-2351',
    status: 'ready',
    summary: 'Os sites que você mais usa, abertos dentro do Orbind.',
    searchPlaceholder: 'Buscar aplicações',
    createLabel: 'Nova aplicação',
    chips: [],
  },
  {
    id: 'portfolio',
    labelKey: 'nav.portfolio',
    fallbackLabel: 'Portfólio',
    icon: 'fas fa-layer-group',
    route: '/portfolio/tree',
    specId: 'PORT-02',
    status: 'ready',
    summary: 'Árvore Solução → Produto → Módulo → Funcionalidade → Ação.',
    searchPlaceholder: 'Buscar no portfólio',
    createLabel: 'Nova solução',
    chips: ['Todos', 'Em andamento', 'Em risco', 'Planejados'],
  },
  {
    // Empresas saiu da Administracao e virou modulo do rail (pedido do
    // usuario 2026-08-25); a tela e a mesma, so mudou de endereco.
    // Empresas e PESSOAS num ícone só (SPEC-20261010-1216-empresas-e-pessoas-num-icone): a tela tem
    // as abas Empresas | Pessoas
    id: 'companies',
    labelKey: 'nav.companies_and_people',
    fallbackLabel: 'Empresas e pessoas',
    icon: 'fas fa-building',
    route: '/companies',
    specId: 'ADM-01',
    status: 'ready',
    summary: 'Empresas, domínios de e-mail e as pessoas de contato.',
    searchPlaceholder: 'Buscar empresas',
    createLabel: 'Nova empresa',
    chips: [],
    ownExplorer: true,
  },
  {
    // Pessoas: contatos das empresas (N:N com funcao, 2026-08-25)
    id: 'people',
    labelKey: 'nav.people',
    fallbackLabel: 'Pessoas',
    icon: 'fas fa-address-book',
    route: '/people',
    specId: 'ADM-01',
    status: 'ready',
    summary: 'Contatos das empresas, com a função de cada pessoa.',
    searchPlaceholder: 'Buscar pessoas',
    createLabel: 'Nova pessoa',
    chips: [],
    ownExplorer: true,
    // aba do ícone "Empresas e pessoas" — sem ícone próprio na barra
    railHidden: true,
    dentroDe: 'companies',
  },
  {
    id: 'channels',
    labelKey: 'nav.communication',
    fallbackLabel: 'Comunicação',
    icon: 'fas fa-comments',
    route: '/channels',
    specId: 'COM-01',
    status: 'ready',
    summary: 'Canais, threads, menções e registro de decisões.',
    searchPlaceholder: 'Pesquisar ou começar uma nova conversa',
    createLabel: 'Nova conversa',
    chips: ['Tudo', 'Não lidas', 'Menções', 'Canais', 'Arquivadas'],
  },
  {
    id: 'groups',
    labelKey: 'nav.groups',
    fallbackLabel: 'Grupos',
    icon: 'fas fa-users',
    route: '/groups',
    specId: 'v2',
    status: 'ready',
    summary: 'Conversas de grupo por time e por assunto.',
    searchPlaceholder: 'Pesquisar grupos',
    createLabel: 'Novo grupo',
    chips: ['Todos', 'Não lidas', 'Menções'],
  },
  {
    id: 'status',
    labelKey: 'nav.status',
    fallbackLabel: 'Status',
    icon: 'far fa-dot-circle',
    route: '/status',
    specId: 'v2',
    status: 'ready',
    summary: 'Meu status publicado e o status da equipe.',
    searchPlaceholder: 'Buscar pessoas',
    createLabel: 'Atualizar status',
    chips: [],
  },
  {
    id: 'broadcast',
    labelKey: 'nav.broadcast',
    fallbackLabel: 'Canais',
    icon: 'fas fa-broadcast-tower',
    route: '/broadcast',
    specId: 'v2',
    status: 'ready',
    summary: 'Canais de avisos — só administradores publicam.',
    searchPlaceholder: 'Buscar canais',
    createLabel: 'Novo canal de avisos',
    chips: ['Seguindo', 'Explorar'],
  },
  {
    id: 'automations',
    labelKey: 'nav.automations',
    fallbackLabel: 'Automações',
    icon: 'fas fa-bolt',
    route: '/automations',
    specId: 'AUT-01',
    status: 'ready',
    summary: 'Regras gatilho → condição → ação e histórico de execuções.',
    searchPlaceholder: 'Buscar automações',
    createLabel: 'Nova automação',
    chips: [],
  },
  {
    id: 'analytics',
    labelKey: 'nav.analytics',
    fallbackLabel: 'Métricas',
    icon: 'fas fa-chart-bar',
    route: '/analytics',
    specId: 'MET-01',
    status: 'ready',
    summary: 'SLA, fluxo, throughput e progresso do portfólio.',
    searchPlaceholder: 'Buscar indicadores',
    createLabel: 'Criar',
    chips: [],
  },
  {
    id: 'notifications',
    labelKey: 'nav.notifications',
    fallbackLabel: 'Notificações',
    icon: 'fas fa-bell',
    route: '/notifications',
    specId: 'v2',
    status: 'ready',
    summary: 'Central de notificações agrupada por período.',
    searchPlaceholder: 'Buscar notificações',
    // categorias do protótipo (`_shell.html:2291-2294`)
    createLabel: 'Criar',
    chips: ['Todas', 'Menções', 'Atribuições', 'Convites', 'Prazos', 'Mudanças', 'Automações', 'E-mails'],
    railHidden: true,
  },
  {
    id: 'admin',
    labelKey: 'nav.admin',
    fallbackLabel: 'Administração',
    icon: 'fas fa-cog',
    route: '/admin',
    specId: 'ADM-01',
    status: 'ready',
    summary: 'Cadastros, permissões, traduções, integrações e auditoria.',
    searchPlaceholder: 'Buscar cadastros',
    createLabel: 'Novo cadastro',
    chips: [],
  },
]

/**
 * Modulo cujo `route` casa com o caminho atual.
 *
 * Compara o primeiro segmento (`/work-items/123` → `work-items`) e, entre os
 * que casam, o de rota mais longa vence.
 */
export function activeModule(pathname: string): ModuleEntry | undefined {
  const segmento = pathname.split('/')[1] ?? ''
  if (segmento === '') return undefined
  const achado = [...MODULES]
    .filter((m) => m.route.split('/')[1] === segmento)
    .sort((a, b) => b.route.length - a.route.length)[0]
  // módulo que mora dentro do ícone de outro acende o ícone dele
  return achado?.dentroDe ? (MODULES.find((m) => m.id === achado.dentroDe) ?? achado) : achado
}
